import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { fmtINR, fmtINRCompact } from "@/lib/format";
import {
  Building2, IndianRupee, Ticket, Users, TrendingUp, Search, Filter, CheckSquare, Clock,
  BarChart3, FileSpreadsheet, Radio, Sparkles, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const PLAN_COLORS = { solo: "#64748B", starter: "#2563EB", growth: "#A855F7", professional: "#EC4899", enterprise: "#F97316" };

const STATUS_COLORS = {
  paid: "text-emerald-700 bg-emerald-100",
  trialing: "text-blue-700 bg-blue-100",
  complimentary: "text-purple-700 bg-purple-100",
  suspended: "text-red-700 bg-red-100",
  overdue: "text-amber-700 bg-amber-100",
  pending: "text-muted-foreground bg-muted",
};

const Stat = ({ label, value, sub, icon: Icon, tone = "orange", to }) => {
  const body = (
    <Card className="p-4 rounded-sm bg-card border-border text-foreground hover:bg-muted transition-colors">
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        {Icon && <Icon className={`w-4 h-4 text-${tone}-500`} />}
      </div>
      <div className="text-2xl font-bold mt-2">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1 font-mono">{sub}</div>}
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [entities, setEntities] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [revByPlan, setRevByPlan] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    Promise.all([
      api.get("/admin/dashboard").then((r) => r.data).catch(() => null),
      api.get("/admin/entities").then((r) => r.data?.entities || []).catch(() => []),
      api.get("/admin/approvals?status=pending").then((r) => r.data?.approvals || []).catch(() => []),
      api.get("/admin/revenue-by-plan").then((r) => r.data?.rows || []).catch(() => []),
      api.get("/admin/usage-metrics").then((r) => r.data).catch(() => null),
    ]).then(([o, e, a, rv, u]) => { setOverview(o); setEntities(e); setApprovals(a); setRevByPlan(rv); setUsage(u); setLoading(false); });
  }, []);

  const totals = useMemo(() => {
    const recognised = entities.reduce((s, e) => s + (e.revenue_recognised_mtd_inr || 0), 0);
    const mrrNet = entities.reduce((s, e) => s + (e.mrr_net_inr || 0), 0);
    return {
      recognised, mrrNet,
      paid: entities.filter((e) => e.status === "paid").length,
      trialing: entities.filter((e) => e.status === "trialing").length,
      overdue: entities.filter((e) => e.status === "overdue").length,
    };
  }, [entities]);

  const filtered = entities.filter((e) => {
    if (filter !== "all" && e.status !== filter) return false;
    if (q && !`${e.name} ${e.gstin || ""} ${e.plan_id}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const requestExtension = async (org) => {
    const days = parseInt(window.prompt(`Extend trial for ${org.name} by how many days?`, "7"), 10);
    if (!days || days <= 0) return;
    const reason = window.prompt("Reason (optional)?", "") || "";
    try {
      await api.post("/admin/approvals/trial-extension", { org_id: org.id, days, reason });
      toast.success("Trial extension request logged for approval");
      const r = await api.get("/admin/approvals?status=pending");
      setApprovals(r.data?.approvals || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-dashboard">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// Zenith360-ERP Admin</div>
        <h1 className="text-3xl font-bold mt-1">Command centre</h1>
        <div className="text-muted-foreground text-sm mt-1">Registered entities, apportioned revenue, and approval queue at a glance.</div>
      </div>

      {loading && <div className="text-muted-foreground text-sm">Loading platform data…</div>}

      {!loading && overview && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Stat label="Total entities"  value={overview.tenants.total} icon={Building2} tone="orange" to="/admin/entities" />
            <Stat label="Paying"           value={totals.paid} sub={`${overview.tenants.complimentary} comp · ${overview.tenants.suspended} susp`} icon={Users} tone="emerald" />
            <Stat label="Trialing"          value={totals.trialing} icon={Clock} tone="blue" />
            <Stat label="MRR (net)"         value={fmtINR(totals.mrrNet)} sub={`ARR ${fmtINR(totals.mrrNet * 12)}`} icon={IndianRupee} tone="green" />
            <Stat label="Revenue · MTD"    value={fmtINR(totals.recognised)} sub="apportioned daily" icon={TrendingUp} tone="green" />
            <Stat label="Approvals pending" value={approvals.length} icon={CheckSquare} tone="amber" to="/admin/approvals" />
          </div>

          {/* Platform usage · current month */}
          {usage && (
            <Card className="rounded-sm bg-card border-border text-foreground p-5" data-testid="admin-usage-tile">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// Platform usage · {usage.month}</div>
                  <div className="font-heading font-semibold text-lg mt-0.5">This month across every tenant</div>
                </div>
                <Link to="/admin/usage" className="text-xs text-orange-600 hover:underline">Drill-down →</Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { k: "invoices_this_month",    label: "Sale invoices",    icon: FileSpreadsheet, tone: "orange" },
                  { k: "gsp_api_calls",          label: "GSP API calls",    icon: Radio,           tone: "blue"   },
                  { k: "ai_credits_used",        label: "AI credits used",  icon: Sparkles,        tone: "purple" },
                  { k: "users_added_this_month", label: "Users added",      icon: UserPlus,        tone: "emerald"},
                ].map(({ k, label, icon: Icon, tone }) => (
                  <div key={k} className="p-3 rounded-sm bg-muted/70 border border-border" data-testid={`admin-usage-${k}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-mono uppercase text-muted-foreground">{label}</div>
                      <Icon className={`w-4 h-4 text-${tone}-500`} />
                    </div>
                    <div className="text-2xl font-bold mt-2">{(usage.totals?.[k] || 0).toLocaleString("en-IN")}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Revenue by plan — waterfall */}
          <Card className="rounded-sm bg-card border-border text-foreground p-5" data-testid="admin-rev-by-plan">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// Revenue by plan · MRR (net of discount)</div>
                <div className="font-heading font-semibold text-lg mt-0.5">Where the money's coming from</div>
              </div>
              <div className="text-[10px] font-mono text-muted-foreground">Bars use the same colour palette as your brand gradient</div>
            </div>
            {revByPlan.length === 0 && <div className="text-muted-foreground text-sm py-8 text-center">No paying entities yet.</div>}
            {revByPlan.length > 0 && (
              <div className="grid md:grid-cols-3 gap-5">
                <div className="md:col-span-2 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revByPlan} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.12} />
                      <XAxis dataKey="plan_name" stroke="currentColor" strokeOpacity={0.6} tick={{ fontSize: 11, fill: "currentColor" }} />
                      <YAxis stroke="currentColor" strokeOpacity={0.6} tick={{ fontSize: 11, fill: "currentColor" }}
                                tickFormatter={fmtINRCompact} />
                      <Tooltip
                        cursor={{ fill: "currentColor", fillOpacity: 0.05 }}
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))",
                                          borderRadius: 2, fontSize: 12, color: "hsl(var(--foreground))" }}
                        formatter={(v, k) => [fmtINR(v), k === "mrr_net" ? "MRR (net)" : k]} />
                      <Bar dataKey="mrr_net" radius={[3, 3, 0, 0]}>
                        {revByPlan.map((r) => (
                          <Cell key={r.plan_id} fill={PLAN_COLORS[r.plan_id] || "#64748B"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {revByPlan.map((r) => (
                    <div key={r.plan_id} className="flex items-center justify-between p-2.5 rounded-sm bg-card border border-border">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PLAN_COLORS[r.plan_id] || "#64748B" }} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{r.plan_name}</div>
                          <div className="text-[10px] font-mono text-muted-foreground">{r.count} entities · {fmtINR(r.price_inr)}/mo list</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono font-semibold">{fmtINR(r.mrr_net)}</div>
                        <div className="text-[10px] font-mono text-muted-foreground/80">ARR {fmtINR(r.arr_net)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Registered entities table */}
          <Card className="rounded-sm bg-card border-border text-foreground">
            <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// Registered entities</div>
                <div className="font-heading font-semibold text-lg mt-0.5">Every company on the platform</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/80" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / GSTIN / plan…"
                    className="h-8 rounded-sm bg-background text-foreground border-border pl-8 text-xs w-64"
                    data-testid="admin-entities-search" />
                </div>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="h-8 rounded-sm bg-background text-foreground border-border text-xs w-36" data-testid="admin-entities-filter">
                    <Filter className="w-3 h-3 mr-1" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["all", "paid", "trialing", "complimentary", "suspended", "overdue", "pending"].map((s) => (
                      <SelectItem key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1100px]">
                <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left p-3">Entity</th>
                    <th className="text-left p-3">Plan</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">MRR (net)</th>
                    <th className="text-right p-3">Revenue MTD</th>
                    <th className="text-right p-3">Users</th>
                    <th className="text-left p-3">Trial ends</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No entities match.</td></tr>
                  )}
                  {filtered.map((e) => (
                    <tr key={e.id} className="border-b border-border/60 hover:bg-muted/40" data-testid={`admin-entity-${e.id}`}>
                      <td className="p-3">
                        <Link to={`/admin/tenants/${e.id}`} className="font-medium hover:text-orange-600">{e.name}</Link>
                        <div className="text-[10px] font-mono text-muted-foreground/80 mt-0.5">
                          {e.is_agency ? "🏢 Agency" : e.entity_type} · {e.gstin || "no GSTIN"}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="capitalize">{e.plan_name || e.plan_id}</div>
                        <div className="text-[10px] font-mono text-muted-foreground/80">{fmtINR(e.plan_price_inr)}/mo{e.discount_pct > 0 && ` · -${e.discount_pct}%`}</div>
                      </td>
                      <td className="p-3">
                        <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest ${STATUS_COLORS[e.status] || STATUS_COLORS.pending}`}>
                          {e.status}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono">{fmtINR(e.mrr_net_inr)}</td>
                      <td className="p-3 text-right font-mono text-emerald-600">{fmtINR(e.revenue_recognised_mtd_inr)}</td>
                      <td className="p-3 text-right font-mono">{e.users_count}</td>
                      <td className="p-3 text-[11px] text-muted-foreground">
                        {e.trial_ends_at ? new Date(e.trial_ends_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {e.status === "trialing" && (
                          <Button size="sm" variant="ghost" onClick={() => requestExtension(e)}
                                  className="h-7 rounded-sm text-blue-600 hover:text-blue-700 hover:bg-blue-400/10 text-xs"
                                  data-testid={`admin-req-trial-${e.id}`}>
                            Extend
                          </Button>
                        )}
                        <Link to={`/admin/tenants/${e.id}`}
                              className="ml-2 text-[11px] text-orange-600 hover:underline">Manage →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-border text-[11px] font-mono text-muted-foreground">
              {filtered.length} of {entities.length} entities · MRR (net) sums to {fmtINR(totals.mrrNet)} · Recognised MTD {fmtINR(totals.recognised)}
            </div>
          </Card>

          {/* Approvals pending strip */}
          {approvals.length > 0 && (
            <Card className="rounded-sm bg-amber-500/5 border-amber-500/25 text-foreground p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-amber-600">// Approvals pending ({approvals.length})</div>
                <Link to="/admin/approvals" className="text-xs text-amber-600 hover:underline">Review all →</Link>
              </div>
              <div className="space-y-1.5 text-sm">
                {approvals.slice(0, 4).map((a) => (
                  <div key={a.id} className="flex items-center justify-between">
                    <div>
                      <span className="uppercase font-mono text-[10px] text-amber-600/80 mr-2">{a.kind}</span>
                      <span className="font-medium">{a.org_name}</span>
                      {a.days && <span className="text-muted-foreground ml-2">· {a.days} days</span>}
                      {a.discount_pct && <span className="text-muted-foreground ml-2">· {a.discount_pct}%</span>}
                      {a.reason && <span className="text-muted-foreground/80 italic ml-2">"{a.reason}"</span>}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground">by {a.requested_by}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
